import { action, computed, observable, autorun, reaction } from 'mobx';
import { Store } from './mainStore';
import EngineStore from '@/store/engineStore';
import { AccessToken } from '@/store/engine/types/Auth';
import { User } from '@/store/engine/openapi/generated';
import UtilsStore from '@/store/utilsStore';
import Cookies from 'js-cookie';

export type MobileAppRedirectData = {
	refreshToken: string;
	refreshTokenExpiresAt: number;
	storeID?: string;
};

export class AuthState {
	@observable loggedIn = false;

	@observable clientID: string;

	constructor(clientID: string) {
		this.clientID = clientID;
	}

	@action.bound
	public setLoggedIn(val: boolean) {
		this.loggedIn = !!val;
	}
}

export interface AuthStoreOptions {
	operatorClientID: string;
	customerClientID: string;
	engine: EngineStore;
}

export default class AuthStore {
	@observable operator: AuthState;
	@observable customer: AuthState;

	@observable engineURI: string | null = null;
	@observable customerURI: string | null = null;
	@observable refreshTokenExchanged = false;
	@observable connecting = false;
	@observable employeeStores: any = [];
	@observable storeSelectionModal = false;
	@observable chosenStore: any = '';
	@observable loginErrorMsg = 'Login error. Not enough permissions.';
	@observable loginError = false;
	@observable frameURL = {
		src: 'none'
	};
	@observable exitModal = false;
	@observable agentError = false;
	@observable revokeTime = 0;

	@observable authUri = '';

	engine: EngineStore;
	store: Store;

	constructor(options: AuthStoreOptions, st: Store) {
		this.operator = new AuthState(options.operatorClientID);
		this.customer = new AuthState(options.customerClientID);
		this.engine = options.engine;
		this.store = st;

		reaction(
			() => this.operator.loggedIn,
			(s) => console.log(s ? 'Operator logged in' : 'Operator not logged in')
		);
		reaction(
			() => this.customer.loggedIn,
			(s) => console.log(s ? 'Customer logged in' : 'Customer not logged in')
		);
	}

	@computed
	public get token() {
		return this.engine.auth.rawToken;
	}

	public wait(howlong: number) {
		return new Promise((r) => setTimeout(r, howlong));
	}

	public handleAgentError() {
		this.logout();
		this.agentError = false;
	}

	@action.bound
	public async handleLoginError() {
		localStorage.removeItem('storeID');
		localStorage.removeItem('refresh_token');
		localStorage.removeItem('refresh_expiration');
		this.chosenStore = null;
		this.loginError = false;
		await this.store.engine.auth.logout();
	}

	public logout() {
		localStorage.removeItem('refresh_token');
		localStorage.removeItem('refresh_expiration');
		localStorage.removeItem('storeID');
		Cookies.remove('mobile-app-redirect-data');
		this.chosenStore = null;
		this.frameURL.src = 'none';
		this.exitModal = false;
		this.operator.setLoggedIn(false);
		this.refreshTokenExchanged = false;
		this.init();
	}

	public endSession() {
		try {
			this.frameURL.src = 'none';
			this.exitModal = false;
			this.operator.setLoggedIn(false);
			this.refreshTokenExchanged = false;
			this.init();
		} catch (e) {
			console.log(e);
		}
	}

	public async waitForOperatorLoggedIn() {
		if (this.operator.loggedIn) {
			return true;
		}
		await UtilsStore.wait(100);
		return this.waitForOperatorLoggedIn();
	}

	public refreshExpired() {
		let exp = localStorage.getItem('refresh_expiration');

		const mobileAppRedirectData = Cookies.get('mobile-app-redirect-data');
		if (mobileAppRedirectData) {
			const object = JSON.parse(mobileAppRedirectData) as MobileAppRedirectData;
			exp = object.refreshTokenExpiresAt.toString();
		}

		if (exp) {
			if (new Date().getTime() > parseInt(exp) * 1000) {
				localStorage.removeItem('refresh_expiration');
				localStorage.removeItem('refresh_token');
				Cookies.remove('mobile-app-redirect-data');

				location.reload();
				return true;
			}
		}

		return false;
	}

	public async init() {
		this.connecting = true;
		const auth = await this.store.engine.auth;
		const code = sessionStorage.getItem('code');
		let refreshToken = localStorage.getItem('refresh_token');
		// @ts-ignore
		window.cookies = Cookies;
		const mobileAppRedirectData = Cookies.get('mobile-app-redirect-data');
		if (mobileAppRedirectData) {
			const object = JSON.parse(mobileAppRedirectData) as MobileAppRedirectData;

			if (object.refreshTokenExpiresAt) {
				localStorage.setItem('refresh_expiration', object.refreshTokenExpiresAt.toString());
			}

			if (object.refreshToken) {
				localStorage.setItem('refresh_token', object.refreshToken);
				refreshToken = object.refreshToken;
			}

			if (object.storeID) {
				localStorage.setItem('storeID', object.storeID);
			}
		}

		if (refreshToken) {
			this.refreshExpired();

			auth.refreshToken = refreshToken;

			try {
				const res = await auth.exchangeRefreshToken();
				await this.operatorLoggedInHandler(res);

				if (auth.sessionEndsAt - new Date().getTime() <= auth.sessionEndsAt - new Date().getTime() - 3600000) {
					this.store.utils.automaticLogout = true;
					localStorage.removeItem('refresh_token');
					localStorage.removeItem('refresh_expiration');
					Cookies.remove('mobile-app-redirect-data');
				} else {
					setTimeout(() => {
						this.store.utils.automaticLogout = true;
						localStorage.removeItem('refresh_token');
						localStorage.removeItem('refresh_expiration');
						Cookies.remove('mobile-app-redirect-data');
					}, auth.sessionEndsAt - new Date().getTime() - 3600000);
				}

				if (auth.sessionEndsAt - new Date().getTime() <= auth.sessionEndsAt - new Date().getTime() - 1800000) {
					this.store.utils.criticalAutomaticLogout = true;
					localStorage.removeItem('refresh_token');
					localStorage.removeItem('refresh_expiration');
					Cookies.remove('mobile-app-redirect-data');
				} else {
					setTimeout(() => {
						this.store.utils.criticalAutomaticLogout = true;
						localStorage.removeItem('refresh_token');
						localStorage.removeItem('refresh_expiration');
						Cookies.remove('mobile-app-redirect-data');
					}, auth.sessionEndsAt - new Date().getTime() - 1800000);
				}

				await this.operatorLoggedInHandler(res);
			} catch (e) {
				localStorage.removeItem('refresh_token');
				localStorage.removeItem('refresh_expiration');
				Cookies.remove('mobile-app-redirect-data');

				this.init();
			}
		}

		if (code) {
			try {
				const $deprecatedEngineJWT = await auth.getToken({ code });
				if ($deprecatedEngineJWT) {
					await this.operatorLoggedInHandler($deprecatedEngineJWT);
				} else {
					throw new Error('No access token');
				}
				sessionStorage.removeItem('code');
			} catch (e) {
				console.error(e);
				console.warn('Something went wrong while exchanging code. Retrying...');
				sessionStorage.removeItem('code');
				localStorage.removeItem('storeID');
				this.init();
			}
		}

		if (!code && !refreshToken) {
			this.connecting = false;
		}

		await this.wait(500);

		if (this.operator.loggedIn) await this.store.engine.store.getDoctorsByStoreCache();
		this.engineURI = auth.getLoginUri(undefined, ['offline_access']);
		this.store.agent.setAgentUri(window.appConfig.VUE_APP_USBAGENT_BASE_URI);
	}

	public async operatorLoggedInHandler(engineJWT: AccessToken) {
		try {
			await this.store.engine.setEngineClient(engineJWT.originalResponse.access_token);

			const employeeStores = await this.store.engine.store.getEmployeeStores();

			this.employeeStores = employeeStores;

			if (!employeeStores || employeeStores.length < 1) {
				this.loginErrorMsg = 'This account does not have any sites configured';
				throw new Error('This account does not have any sites configured');
			}

			const localStorageStoreID = localStorage.getItem('storeID');
			const localStorageStore = employeeStores.find((i) => i && i.storeID === localStorageStoreID);
			if (!localStorageStoreID || !localStorageStore) {
				if (this.employeeStores.length < 2) {
					this.chosenStore = this.employeeStores[0];
				} else {
					this.storeSelectionModal = true;
				}
			} else {
				this.chosenStore = localStorageStore;
			}

			await new Promise(async (r) => {
				// eslint-disable-next-line no-constant-condition
				while (1) {
					await new Promise((rr) => setTimeout(rr, 50));
					if (this.chosenStore) {
						r();
						break;
					}
				}
			});

			if (!this.chosenStore) {
				throw new Error('STORE NOT FOUND');
			}

			const waitForStore = async () => {
				const s = this.store.engine.store.setStore(this.chosenStore.storeID);
				const tm = new Promise((r) => setTimeout(r, 10000, 'timeout'));

				const race = await Promise.race([s, tm]);

				if (race === 'timeout') {
					await waitForStore();
				}
			};

			try {
				await waitForStore();
			} catch (e) {
				console.error(e);
			}

			const doctor = (await this.store.engine.profile.getUser(engineJWT.parsedToken.user_id)) as User;
			this.store.doctor.doctorUser = doctor;
			document.title = 'ARIZE';
			this.operator.setLoggedIn(true);

			if (sessionStorage.getItem('code')) {
				sessionStorage.removeItem('code');
			}
			this.connecting = false;
		} catch (e) {
			this.connecting = false;
			this.operator.setLoggedIn(false);
			this.loginError = true;
			console.error(e);
		}
	}
}
